import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../sanityClient";

const POSTS_QUERY = `*[_type == "post"] | order(publishedAt desc) {
  _id,
  title,
  slug,
  publishedAt,
  excerpt,
  "mainImageUrl": mainImage.asset->url,
  "authorName": author->name,
  "authorImageUrl": author->image.asset->url,
  "categories": categories[]->title
}`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.fetch(POSTS_QUERY).then((data) => {
      setPosts(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0f1117] text-white">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 pt-20 pb-12">
        <div className="mb-2 text-emerald-400 text-sm font-semibold tracking-widest uppercase">
          Diet Reg Blog
        </div>
        <h1 className="text-5xl font-bold leading-tight mb-4">
          Nutrition & <span className="text-emerald-400">Fitness Insights</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-xl">
          Science-backed tips on meal tracking, macros, workouts, and building
          sustainable habits.
        </p>
      </div>

      {/* Posts */}
      <div className="max-w-4xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-40 rounded-2xl bg-white/5 animate-pulse"
              />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            No posts yet. Stay tuned!
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <PostCard key={post._id} post={post} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PostCard({ post }) {
  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <Link
      to={`/blog/${post.slug?.current}`}
      className="group flex gap-6 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/40 rounded-2xl p-6 transition-all duration-300"
    >
      {/* Thumbnail */}
      {post.mainImageUrl && (
        <img
          src={post.mainImageUrl}
          alt={post.title}
          className="w-32 h-28 object-cover rounded-xl flex-shrink-0"
        />
      )}

      {/* Content */}
      <div className="flex flex-col justify-between flex-1 min-w-0">
        <div>
          {/* Categories */}
          {post.categories?.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-2">
              {post.categories.map((cat) => (
                <span
                  key={cat}
                  className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/20"
                >
                  {cat}
                </span>
              ))}
            </div>
          )}

          <h2 className="text-xl font-semibold text-white group-hover:text-emerald-300 transition-colors line-clamp-2 mb-2">
            {post.title}
          </h2>

          {post.excerpt && (
            <p className="text-gray-400 text-sm line-clamp-2">{post.excerpt}</p>
          )}
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 mt-4">
          {post.authorImageUrl && (
            <img
              src={post.authorImageUrl}
              alt={post.authorName}
              className="w-6 h-6 rounded-full object-cover"
            />
          )}
          {post.authorName && (
            <span className="text-gray-500 text-xs">{post.authorName}</span>
          )}
          {date && (
            <>
              <span className="text-gray-600 text-xs">·</span>
              <span className="text-gray-500 text-xs">{date}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
